import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { useIntl } from "../../contexts/IntlContext";

interface Series {
  label: string;
  data: number[];
  color?: string;
}

type MetricsTableProps = {
  data: Series[];
  displayData: Series[];
  total: number;
  maxDisplayed?: number;
  onRowClick?: (label: string) => void;
  selectedSeries?: string | null;
  hasError?: boolean;
  stickyHeader?: boolean;
  stickyFooter?: boolean;
  showOmittedMessage?: boolean;
  showEmptyState?: boolean;
  containerSx?: SxProps<Theme>;
  tableSx?: SxProps<Theme>;
  fillerRow?: boolean;
  /** Cabeçalho da 1ª coluna (padrão: câmera). */
  nameHeader?: string;
};

export default function MetricsTable({
  data,
  displayData,
  total,
  maxDisplayed,
  onRowClick,
  selectedSeries,
  hasError,
  stickyHeader,
  stickyFooter,
  showOmittedMessage,
  showEmptyState,
  containerSx,
  tableSx,
  fillerRow,
  nameHeader,
}: MetricsTableProps) {
  const intl = useIntl();

  return (
    <TableContainer sx={containerSx}>
      <Table padding="none" size="small" stickyHeader={stickyHeader} sx={tableSx}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ py: 0.5, px: 1 }}>
              {nameHeader ?? intl.formatMessage({ id: "metricChart.channel" })}
            </TableCell>
            <TableCell sx={{ py: 0.5, px: 1, width: "70px" }}>
              {intl.formatMessage({ id: "metricChart.total" })}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {showEmptyState && displayData.length === 0 && (
            <TableRow>
              <TableCell colSpan={2} sx={{ py: 2, px: 1.5, textAlign: "center" }}>
                {hasError ? (
                  <Alert severity="warning">
                    {intl.formatMessage({ id: "metricChart.errorState" })}
                  </Alert>
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {intl.formatMessage({ id: "metricChart.noData" })}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          )}
          {showOmittedMessage && maxDisplayed !== undefined && data.length > maxDisplayed && (
            <TableRow>
              <TableCell colSpan={2} sx={{ p: 1, textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                  {intl.formatMessage(
                    { id: "metricChart.metricsOmitted" },
                    { count: data.length - maxDisplayed },
                  )}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {displayData.map((series) => (
            <TableRow
              key={series.label}
              {...(onRowClick ? { onClick: () => onRowClick(series.label), sx: { cursor: "pointer" } } : {})}
              sx={{
                ...(onRowClick ? { cursor: "pointer" } : {}),
                backgroundColor:
                  selectedSeries === series.label ? "action.selected" : "transparent",
                opacity:
                  selectedSeries && selectedSeries !== series.label ? 0.5 : 1,
                "&:hover": onRowClick ? { backgroundColor: "action.hover" } : {},
              }}
            >
              <TableCell
                sx={{
                  py: 0.5,
                px: 1,
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: series.color,
                      borderRadius: "50%",
                      mr: 0.5,
                      flexShrink: 0,
                    }}
                  />
                  {series.label}
                </Box>
              </TableCell>
              <TableCell sx={{ py: 0.5, px: 1, width: "70px" }}>
                {series.data.reduce((sum, value) => sum + value, 0)}
              </TableCell>
            </TableRow>
          ))}
          {fillerRow && (
            <TableRow sx={{ height: "100%" }}>
              <TableCell colSpan={2} sx={{ padding: 0, border: "none" }} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell
              sx={{
                fontWeight: "bold",
                py: 0.5,
                px: 1,
                ...(stickyFooter
                  ? { position: "sticky", bottom: 0, backgroundColor: "background.paper" }
                  : {}),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ width: 12, height: 12, mr: 0.5, flexShrink: 0 }} />
                {intl.formatMessage({ id: "metricChart.grandTotal" })}
              </Box>
            </TableCell>
            <TableCell
              sx={{
                fontWeight: "bold",
                py: 0.5,
                px: 1,
                width: "70px",
                ...(stickyFooter
                  ? { position: "sticky", bottom: 0, backgroundColor: "background.paper" }
                  : {}),
              }}
            >
              {total}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
